/**
 * Crafting page for a hex.
 * Uses HexLayout for consistent navigation with the hex view page.
 * Allows users to craft items from learned blueprints or building-granted recipes.
 */

import { useState, useEffect } from 'react';
import { router } from '@inertiajs/react';
import {
    CraftingPageProps,
    CraftableRecipe,
    UserResources,
    RESOURCE_LABELS,
    RESOURCE_COLORS,
    formatDuration,
} from '@/types/crafting';
import { getRarityColorClass } from '@/types/items';
import HexLayout from '@/layouts/HexLayout';
import { HexResources } from '@/components/HexResources';
import { HexQuickLinks } from '@/components/HexQuickLinks';

/** Context passed to sidebar render function. */
interface SidebarContext {
    closeSidebar: () => void;
    isMobile: boolean;
}

export default function Crafting({
    hex,
    recipes,
    currentJob,
    userResources,
    isOwner,
    canCollect,
    nextCollectionAt,
}: CraftingPageProps) {
    const [selectedRecipe, setSelectedRecipe] = useState<CraftableRecipe | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [isCollecting, setIsCollecting] = useState(false);
    const [isCollectingResources, setIsCollectingResources] = useState(false);
    const [minutesUntilCollection, setMinutesUntilCollection] = useState<number | null>(null);

    // Timer for live countdown updates
    const [timeRemaining, setTimeRemaining] = useState<number | null>(
        currentJob?.time_remaining_seconds ?? null
    );

    // Calculate total crafting time in seconds from started_at and completes_at
    const totalCraftingSeconds = currentJob
        ? Math.max(1, Math.floor(
            (new Date(currentJob.completes_at).getTime() - new Date(currentJob.started_at).getTime()) / 1000
          ))
        : 1;

    // Update timer every second when there's an active job
    useEffect(() => {
        if (!currentJob || currentJob.is_complete) {
            setTimeRemaining(null);
            return;
        }

        setTimeRemaining(currentJob.time_remaining_seconds);

        const interval = setInterval(() => {
            setTimeRemaining((prev) => {
                if (prev === null || prev <= 0) {
                    // Refresh the page when complete
                    router.reload({ only: ['currentJob', 'userResources'] });
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [currentJob]);

    // Resource collection timer
    useEffect(() => {
        if (canCollect || !nextCollectionAt) {
            setMinutesUntilCollection(null);
            return;
        }

        const updateTimer = () => {
            const nextTime = new Date(nextCollectionAt).getTime();
            const now = Date.now();
            const diff = Math.max(0, Math.ceil((nextTime - now) / (1000 * 60)));
            setMinutesUntilCollection(diff);

            // If timer hits 0, refresh page data
            if (diff === 0) {
                router.reload({ only: ['canCollect', 'nextCollectionAt', 'userResources'] });
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 60000); // Update every minute

        return () => clearInterval(interval);
    }, [canCollect, nextCollectionAt]);

    const handleCollectResources = () => {
        if (!canCollect || isCollectingResources) return;

        setIsCollectingResources(true);
        router.post(
            `/hex/${hex.id}/collect`,
            {},
            {
                preserveScroll: true,
                onFinish: () => setIsCollectingResources(false),
            }
        );
    };

    const handleStartCrafting = () => {
        if (!selectedRecipe || isSubmitting) return;

        setIsSubmitting(true);
        router.post(
            `/hex/${hex.id}/crafting`,
            { item_recipe_id: selectedRecipe.id },
            {
                onFinish: () => {
                    setIsSubmitting(false);
                    setSelectedRecipe(null);
                },
            }
        );
    };

    const handleCancelCrafting = () => {
        if (!currentJob || isCancelling) return;

        setIsCancelling(true);
        router.delete(`/hex/${hex.id}/crafting`, {
            onFinish: () => setIsCancelling(false),
        });
    };

    const handleCollect = () => {
        if (!currentJob || !currentJob.is_complete || isCollecting) return;

        setIsCollecting(true);
        router.post(`/hex/${hex.id}/crafting/collect`, {}, {
            onFinish: () => setIsCollecting(false),
        });
    };

    // Check if job is complete (either from server or timer reached 0)
    const isJobComplete = currentJob?.is_complete || (timeRemaining !== null && timeRemaining <= 0);

    // Calculate progress percentage using total crafting time (not initial time remaining)
    // This ensures the progress bar starts at the correct position on page load
    const progressPercent = currentJob
        ? Math.max(0, Math.min(100, 100 - ((timeRemaining ?? 0) / totalCraftingSeconds) * 100))
        : 0;

    // Sidebar render function for HexLayout
    const renderSidebar = ({ closeSidebar, isMobile }: SidebarContext) => (
        <>
            {/* Quick Navigation Links - Collapsible */}
            <HexQuickLinks
                hexId={hex.id}
                isOwner={isOwner}
                activeItem="crafting"
                collapsible
                closeSidebar={closeSidebar}
                isMobile={isMobile}
            />

            {/* Current Crafting Job Status (if any) */}
            {currentJob && (
                <div className="p-4 border-b border-slate-700">
                    <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                        {isJobComplete ? 'Ready to Collect' : 'In Progress'}
                    </h2>
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            {currentJob.recipe.image.small && (
                                <img
                                    src={currentJob.recipe.image.small}
                                    alt={currentJob.recipe.name}
                                    className="w-8 h-8 object-cover rounded border border-slate-600"
                                />
                            )}
                            <span className={`text-sm font-medium ${getRarityColorClass(currentJob.recipe.rarity.value as any)}`}>
                                {currentJob.recipe.name}
                            </span>
                        </div>
                        <div className="text-sm text-gray-400">
                            {isJobComplete
                                ? 'Complete!'
                                : formatDuration(timeRemaining ?? 0)}
                        </div>
                        {/* Mini progress bar */}
                        <div className="w-full h-1.5 bg-slate-700 rounded-full overflow-hidden">
                            <div
                                className={`h-full transition-all duration-1000 ${isJobComplete ? 'bg-green-500' : 'bg-purple-500'}`}
                                style={{ width: `${isJobComplete ? 100 : progressPercent}%` }}
                            />
                        </div>
                        {/* Mini collect button in sidebar */}
                        {isJobComplete && (
                            <button
                                onClick={handleCollect}
                                disabled={isCollecting}
                                className="w-full mt-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded transition-colors disabled:opacity-50"
                            >
                                {isCollecting ? 'Collecting...' : 'Collect'}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Resource Collection Section */}
            <div className="p-4 border-b border-slate-700">
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    Resource Collection
                </h2>

                {/* Collection button when resources are available */}
                {canCollect && (
                    <button
                        onClick={handleCollectResources}
                        disabled={isCollectingResources}
                        className="w-full px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-800 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors mb-3"
                    >
                        {isCollectingResources ? 'Collecting...' : 'Collect Resources'}
                    </button>
                )}

                {/* Timer showing when next collection is available */}
                {!canCollect && (
                    <div className="text-center">
                        <p className="text-xs text-gray-400 mb-1">Next collection in:</p>
                        <p className="text-lg font-semibold text-yellow-400">
                            {minutesUntilCollection !== null
                                ? `${minutesUntilCollection} min${minutesUntilCollection !== 1 ? 's' : ''}`
                                : 'Calculating...'}
                        </p>
                    </div>
                )}
            </div>

            {/* Your Resources */}
            <div className="p-4">
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    Your Resources
                </h2>
                <HexResources resources={userResources} compact showHeaders={false} />
            </div>
        </>
    );

    return (
        <HexLayout
            title={`Crafting - ${hex.name}`}
            hex={hex}
            isOwner={isOwner}
            sidebar={renderSidebar}
        >
            {/* Main content area */}
            <div className="p-4 md:p-8">
                <div className="max-w-4xl space-y-6">
                    {/* Current Crafting Job */}
                    {currentJob && (
                        <div className={`bg-slate-800 rounded-lg p-6 border ${isJobComplete ? 'border-green-500/50' : 'border-purple-500/50'}`}>
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <h2 className="text-xl font-bold text-white mb-1">
                                        {isJobComplete ? 'Ready to Collect!' : 'Crafting in Progress'}
                                    </h2>
                                    <p className="text-gray-400">
                                        {isJobComplete ? 'Crafted: ' : 'Creating: '}
                                        <span className={getRarityColorClass(currentJob.recipe.rarity.value as any)}>
                                            {currentJob.recipe.name}
                                        </span>
                                    </p>
                                </div>
                                {currentJob.recipe.image.medium && (
                                    <img
                                        src={currentJob.recipe.image.medium}
                                        alt={currentJob.recipe.name}
                                        className="w-16 h-16 object-cover rounded-lg border border-slate-600"
                                    />
                                )}
                            </div>

                            {/* Progress Bar */}
                            <div className="mb-4">
                                <div className="flex justify-between text-sm text-gray-400 mb-2">
                                    <span>Progress</span>
                                    <span>
                                        {isJobComplete
                                            ? 'Complete!'
                                            : formatDuration(timeRemaining ?? 0)}
                                    </span>
                                </div>
                                <div className="w-full h-4 bg-slate-700 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full transition-all duration-1000 ${
                                            isJobComplete
                                                ? 'bg-gradient-to-r from-green-600 to-green-400'
                                                : 'bg-gradient-to-r from-purple-600 to-purple-400'
                                        }`}
                                        style={{ width: `${isJobComplete ? 100 : progressPercent}%` }}
                                    />
                                </div>
                            </div>

                            {/* Resources Spent */}
                            <div className="mb-4">
                                <p className="text-sm text-gray-400 mb-2">Resources Invested:</p>
                                <div className="flex flex-wrap gap-3">
                                    {Object.entries(currentJob.resources_spent)
                                        .filter(([, amount]) => amount && amount > 0)
                                        .map(([type, amount]) => (
                                            <span
                                                key={type}
                                                className={`text-sm ${RESOURCE_COLORS[type as keyof UserResources]}`}
                                            >
                                                {amount} {RESOURCE_LABELS[type as keyof UserResources]}
                                            </span>
                                        ))}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3">
                                {/* Collect Button - shown when complete */}
                                {isJobComplete && (
                                    <button
                                        onClick={handleCollect}
                                        disabled={isCollecting}
                                        className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                                    >
                                        {isCollecting ? 'Collecting...' : 'Collect Item'}
                                    </button>
                                )}

                                {/* Cancel Button - shown when not complete */}
                                {!isJobComplete && (
                                    <button
                                        onClick={handleCancelCrafting}
                                        disabled={isCancelling}
                                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                                    >
                                        {isCancelling ? 'Cancelling...' : 'Cancel & Refund'}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Recipe List */}
                    {!currentJob && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Available Recipes */}
                            <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
                                <h2 className="text-xl font-bold text-white mb-4">
                                    Available Recipes
                                </h2>

                                {recipes.length === 0 ? (
                                    <div className="text-center py-8 text-gray-400">
                                        <p className="mb-2">No recipes available.</p>
                                        <p className="text-sm">
                                            Learn recipes by obtaining blueprints or constructing buildings.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                                        {recipes.map((recipe) => (
                                            <button
                                                key={recipe.id}
                                                onClick={() => setSelectedRecipe(recipe)}
                                                className={`w-full text-left p-4 rounded-lg border transition-all ${
                                                    selectedRecipe?.id === recipe.id
                                                        ? 'bg-purple-900/30 border-purple-500'
                                                        : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                                                }`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    {recipe.image.small && (
                                                        <img
                                                            src={recipe.image.small}
                                                            alt={recipe.name}
                                                            className="w-12 h-12 object-cover rounded border border-slate-600"
                                                        />
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className={`font-semibold truncate ${getRarityColorClass(recipe.rarity.value as any)}`}>
                                                            {recipe.name}
                                                        </h3>
                                                        <p className="text-sm text-gray-400">
                                                            {recipe.source_name} • {recipe.crafting_time_display}
                                                        </p>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            {!recipe.can_afford && (
                                                                <span className="text-xs text-red-400">
                                                                    Insufficient resources
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Recipe Detail */}
                            <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
                                {selectedRecipe ? (
                                    <>
                                        <div className="flex items-start gap-4 mb-6">
                                            {selectedRecipe.image.medium && (
                                                <img
                                                    src={selectedRecipe.image.medium}
                                                    alt={selectedRecipe.name}
                                                    className="w-24 h-24 object-cover rounded-lg border border-slate-600"
                                                />
                                            )}
                                            <div>
                                                <h2 className={`text-2xl font-bold ${getRarityColorClass(selectedRecipe.rarity.value as any)}`}>
                                                    {selectedRecipe.name}
                                                </h2>
                                                <p className="text-gray-400">
                                                    {selectedRecipe.rarity.label}
                                                </p>
                                                <p className="text-sm text-gray-500 mt-1">
                                                    Source: {selectedRecipe.source_name}
                                                </p>
                                            </div>
                                        </div>

                                        {selectedRecipe.description && (
                                            <p className="text-gray-300 mb-6">
                                                {selectedRecipe.description}
                                            </p>
                                        )}

                                        {/* Crafting Requirements */}
                                        <div className="mb-6">
                                            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                                                Required Materials
                                            </h3>
                                            <div className="space-y-2">
                                                {Object.entries(selectedRecipe.crafting_cost)
                                                    .filter(([, amount]) => amount && amount > 0)
                                                    .map(([type, amount]) => {
                                                        const available = userResources[type as keyof UserResources] ?? 0;
                                                        const canAfford = available >= (amount ?? 0);
                                                        return (
                                                            <div
                                                                key={type}
                                                                className="flex items-center justify-between"
                                                            >
                                                                <span className={RESOURCE_COLORS[type as keyof UserResources]}>
                                                                    {RESOURCE_LABELS[type as keyof UserResources]}
                                                                </span>
                                                                <span className={canAfford ? 'text-green-400' : 'text-red-400'}>
                                                                    {available} / {amount}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </div>

                                        {/* Crafting Time */}
                                        <div className="mb-6">
                                            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                                Crafting Time
                                            </h3>
                                            <p className="text-white text-lg">
                                                {selectedRecipe.crafting_time_display}
                                            </p>
                                        </div>

                                        {/* Item Stats Preview */}
                                        <div className="mb-6">
                                            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                                Item Stats
                                            </h3>
                                            <p className="text-gray-300">
                                                {selectedRecipe.stat_count} stat(s) with values between{' '}
                                                {selectedRecipe.base_stat_range[0]}-{selectedRecipe.base_stat_range[1]}
                                            </p>
                                            <p className="text-sm text-gray-500 mt-1">
                                                Quality is randomly determined when crafting completes.
                                            </p>
                                        </div>

                                        {/* Start Crafting Button */}
                                        <button
                                            onClick={handleStartCrafting}
                                            disabled={!selectedRecipe.can_afford || isSubmitting}
                                            className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isSubmitting
                                                ? 'Starting...'
                                                : selectedRecipe.can_afford
                                                ? 'Start Crafting'
                                                : 'Insufficient Resources'}
                                        </button>
                                    </>
                                ) : (
                                    <div className="text-center py-12 text-gray-400">
                                        <p>Select a recipe to view details</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </HexLayout>
    );
}
